import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { ArrowRight, Building2, Sparkles, TrendingUp, Users, Zap, Target } from 'lucide-react';

const HomePage = () => {
  useEffect(() => {
    document.title = 'Nexus LBE | Formatted, Scalable LBE Platform';
  }, []);

  return (
    <div className="relative">
      {/* Hero Section */}
      <section
        className="relative overflow-hidden pt-16 pb-10 lg:pt-20 lg:pb-14"
        aria-label="Nexus LBE hero"
        data-testid="hero-section"
      >
        <div className="pointer-events-none absolute inset-0 -z-10 gradient-sunset" />
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Left: Content */}
            <div className="space-y-6">
              <div className="space-y-3">
                <h2 className="font-['Geist'] text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">
                  Moments Made to Be Felt.
                </h2>
              </div>
              
              <h1 className="font-['Geist'] tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
                NEXUS LBE is the operating system for the next-generation of location-based entertainment experiences
              </h1>
              
              <p className="font-['Lato'] text-base md:text-lg text-muted-foreground max-w-prose leading-relaxed">
                Our solution is the first repeatable, IP-driven immersive meets live action LBE platform activating dormant real estate, 
                positioning NEXUS LBE as the foundational platform for "LBE as a Service" at scale.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Link to="/product">
                  <Button 
                    size="lg" 
                    className="h-12 px-6 bg-[#00F5FF] hover:bg-[#00F5FF]/90 text-[#0F0B1F] w-full sm:w-auto"
                    data-testid="hero-secondary-cta"
                  >
                    View Platform
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
            
            {/* Right: Hero Image - Aligned with headline */}
            <div>
              <div className="relative rounded-2xl overflow-hidden aspect-[4/3] bg-secondary shadow-2xl">
                <img 
                  src="/images/hero-entrance-v5.png" 
                  alt="NEXUS LBE venue entrance" 
                  className="w-full h-full object-cover"
                  loading="eager"
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-white/40 via-transparent to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Coming Soon Banner */}
      <section className="bg-primary text-primary-foreground py-4">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl">
          <div className="flex items-center justify-center gap-3 text-center">
            <Sparkles className="h-5 w-5" />
            <p className="font-['Lato'] font-medium">
              First NEXUS LBE venue coming soon - e.g. Berlin pilot in development
            </p>
            <Sparkles className="h-5 w-5" />
          </div>
        </div>
      </section>

      {/* Audience Segmentation */}
      <section className="py-10 lg:py-14" data-testid="audience-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl">
          <div className="text-center max-w-3xl mx-auto mb-8">
            <h2 className="font-['Geist'] text-3xl md:text-4xl mb-4">
              Built for three critical partners
            </h2>
            <p className="text-muted-foreground text-base md:text-lg">
              Fast to launch. Lower capex/opex. Fresh titles without rebuilding. Built for IP integrity.
            </p>
          </div>
          
          <Tabs defaultValue="ip" className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-8">
              <TabsTrigger value="ip" data-testid="tabs-ip-owners">IP Owners</TabsTrigger>
              <TabsTrigger value="property" data-testid="tabs-property-partners">Property Partners</TabsTrigger>
              <TabsTrigger value="investors" data-testid="tabs-investors">Investors</TabsTrigger>
            </TabsList>
            
            <TabsContent value="ip">
              <Card>
                <CardContent className="p-8 md:p-12">
                  <div className="grid md:grid-cols-2 gap-8 items-stretch">
                    <div className="space-y-4 flex flex-col">
                      <div className="h-10 flex items-center">
                        <Users className="h-10 w-10 text-primary" />
                      </div>
                      <h3 className="font-['Geist'] text-2xl md:text-3xl">
                        Scale your titles
                      </h3>
                      <p className="text-muted-foreground leading-relaxed">
                        We turn your titles into formatted, multi-city experiences with measurable outcomes. 
                        Protect brand integrity while unlocking new revenue streams and extending IP life cycles.
                      </p>
                      <ul className="space-y-2 text-sm">
                        <li className="flex items-start gap-2">
                          <Target className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>Repeatable format ensures consistent brand experience across all sites</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Target className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>Lower tech costs through narrative-first approach and proven playbooks</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Target className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>Leverages marketing cycles aligned with film, TV, and product launches</span>
                        </li>
                      </ul>
                      <Link to="/ip-owners">
                        <Button className="mt-4" data-testid="ip-owners-cta" onClick={() => window.scrollTo(0, 0)}>
                          Learn More
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                    <div className="relative rounded-xl overflow-hidden bg-secondary h-[400px]">
                      <img 
                        src="/images/ip-partner-v2.png" 
                        alt="IP owners partner visualization - scaling titles globally" 
                        className="w-full h-full object-cover object-center"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="property">
              <Card>
                <CardContent className="p-8 md:p-12">
                  <div className="grid md:grid-cols-2 gap-8 items-stretch">
                    <div className="space-y-4 flex flex-col">
                      <div className="h-10 flex items-center">
                        <Building2 className="h-10 w-10 text-primary" />
                      </div>
                      <h3 className="font-['Geist'] text-2xl md:text-3xl">
                        Activate underused spaces
                      </h3>
                      <p className="text-muted-foreground leading-relaxed">
                        Drive consistent footfall, dwell time, and F&B uplift with a proven attraction footprint 
                        that activates dormant commercial real estate.
                      </p>
                      <ul className="space-y-2 text-sm">
                        <li className="flex items-start gap-2">
                          <Target className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>Standardized ~1,600 sqm format fits multiple property types</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Target className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>Acts as anchor attraction with halo effect for surrounding businesses</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Target className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>Multiple revenue streams: tickets, merchandise, food & beverage</span>
                        </li>
                      </ul>
                      <Link to="/property-partners">
                        <Button className="mt-4" data-testid="property-partners-cta">
                          See Venue Requirements
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                    <div className="relative rounded-xl overflow-hidden bg-secondary h-[400px]">
                      <img 
                        src="/images/property-partner.jpg" 
                        alt="Property partners - activating underused spaces" 
                        className="w-full h-full object-cover object-center"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="investors">
              <Card>
                <CardContent className="p-8 md:p-12">
                  <div className="grid md:grid-cols-2 gap-8 items-stretch">
                    <div className="space-y-4 flex flex-col">
                      <div className="h-10 flex items-center">
                        <TrendingUp className="h-10 w-10 text-primary" />
                      </div>
                      <h3 className="font-['Geist'] text-2xl md:text-3xl">
                        Predictable unit economics
                      </h3>
                      <p className="text-muted-foreground leading-relaxed">
                        A proven, formatted attraction model delivering measurable outcomes with lower capex and 
                        opex than traditional LBE approaches.
                      </p>
                      <ul className="space-y-2 text-sm">
                        <li className="flex items-start gap-2">
                          <Target className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>Formatted approach enables rapid expansion and operational excellence</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Target className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>Diversified revenue streams mitigate risk and maximize returns</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Target className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>Strong IP pipeline ensures continuous flow of fresh content</span>
                        </li>
                      </ul>
                      <Link to="/investors">
                        <Button className="mt-4" data-testid="investors-cta">
                          View Investment Opportunity
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                    <div className="relative rounded-xl overflow-hidden bg-secondary h-[400px]">
                      <img 
                        src="/images/investor-partner.jpg" 
                        alt="Investor partners - predictable unit economics" 
                        className="w-full h-full object-cover object-center"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Problems We Solve */}
      <section className="py-10 lg:py-14 ">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl">
          <div className="text-center max-w-3xl mx-auto mb-8">
            <h2 className="font-['Geist'] text-3xl md:text-4xl mb-4">
              Problems our model solves
            </h2>
            <p className="text-muted-foreground text-base md:text-lg">
              Traditional LBE models face critical challenges. Our formatted platform addresses them systematically.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            <Card>
              <CardContent className="p-6 space-y-3">
                <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Zap className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-['Geist'] text-xl">Risky one-offs</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  <strong>Old model:</strong> High capex, can't scale, knowledge lost between sites.
                  <br /><strong>Nexus:</strong> Formatted system reduces risk, enables rapid multi-city rollout.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6 space-y-3">
                <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Target className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-['Geist'] text-xl">Consumer fatigue</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  <strong>Old model:</strong> Single title runs 6 weeks or 3 years, becomes tourist trap.
                  <br /><strong>Nexus:</strong> Rotating titles keep domestic audiences engaged, refresh quickly.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6 space-y-3">
                <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-['Geist'] text-xl">Inconsistent execution</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  <strong>Old model:</strong> Poor execution leads to failed projects, no playbooks.
                  <br /><strong>Nexus:</strong> Proven 5-phase psychology + operational playbooks ensure success.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Quick Journey Preview */}
      <section className="py-10 lg:py-14">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl">
          <div className="text-center max-w-3xl mx-auto mb-8">
            <h2 className="font-['Geist'] text-3xl md:text-4xl mb-4">
              The 6-room journey
            </h2>
            <p className="text-muted-foreground text-base md:text-lg">
              90 minutes. Actor-guided. Five phases of proven guest psychology.
            </p>
          </div>
          
          <div className="flex justify-center mb-8">
            <Link to="/product">
              <Button size="lg" className="bg-[#00F5FF] hover:bg-[#00F5FF]/90 text-[#0F0B1F]" data-testid="view-full-journey-cta">
                View Full Journey Details
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { name: 'Foyer Fair', img: 'journey-1-v3', ext: 'jpeg' },
              { name: 'Briefing', img: 'journey-2-v2', ext: 'jpg' },
              { name: 'Experience 1', img: 'journey-3-v2', ext: 'jpg' },
              { name: 'Bridge', img: 'journey-4-v3', ext: 'jpeg' },
              { name: 'Climax', img: 'journey-5-v2', ext: 'jpg' },
              { name: 'Celebration', img: 'journey-6-v2', ext: 'jpg' },
            ].map((room, idx) => (
              <div key={idx} className="space-y-2">
                <div className="relative rounded-lg overflow-hidden aspect-square bg-secondary">
                  <img 
                    src={`/images/${room.img}.${room.ext || (room.img === 'journey-2' ? 'jpg' : 'jpeg')}`}
                    alt={`Room ${idx + 1}: ${room.name}`}
                    className="w-full h-full object-cover"
                    loading="eager"
                  />
                </div>
                <p className="text-xs text-center font-['Lato'] text-muted-foreground">
                  {idx + 1}. {room.name}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-10 lg:py-14">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl text-center">
          <h2 className="font-['Geist'] text-3xl md:text-4xl mb-4">
            Ready to explore partnership?
          </h2>
          <p className="text-base md:text-lg mb-8 max-w-2xl mx-auto text-muted-foreground">
            Whether you're an IP owner, property partner, or investor, let's discuss how Nexus LBE 
            can bring your vision to life.
          </p>
          <div className="flex flex-col gap-4 items-center">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button 
                  size="lg" 
                  className="h-12 px-6 w-full sm:w-auto bg-[#FF6B9D] hover:bg-[#FF6B9D]/90 text-white"
                  data-testid="final-cta-contact"
                >
                  Get in Touch
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/roadmap">
                <Button 
                  size="lg" 
                  className="h-12 px-6 w-full sm:w-auto bg-[#00F5FF] hover:bg-[#00F5FF]/90 text-[#0F0B1F]"
                  data-testid="final-cta-roadmap"
                >
                  View Roadmap
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
